import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../components/ui/button";
import OrdenCompraForm from "../../components/ui/OrdenForm";
import PageSizeSelect from "../../components/ui/PageSizeSelect";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { LoaderPinwheel, Search, X } from "lucide-react";

const ESTATUS_STYLES = {
  Creada: "bg-yellow-50 text-yellow-700 border border-yellow-200",
  Pendiente: "bg-orange-50 text-orange-700 border border-orange-200",
  Autorizada: "bg-blue-50   text-blue-700   border border-blue-200",
  Pagada: "bg-green-50  text-green-700  border border-green-200",
  Cerrada: "bg-gray-100  text-gray-600   border border-gray-200",
  Rechazada: "bg-red-50    text-red-700    border border-red-200",
};

const OrdenesPage = () => {
  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [itemsPerPage, setItemsPerPage] = useState(10);
  const token = localStorage.getItem("token");
  const API_URL = process.env.REACT_APP_API_URL + "/ordenes";
  const navigate = useNavigate();

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrdenes(res.data);
    } catch {
      toast.error("No se pudieron cargar las órdenes de compra");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line
  }, []);
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, ordenes]);

  const filteredOrdenes = ordenes.filter((o) => {
    const term = searchTerm.toLowerCase();
    return [
      o.descripcion,
      o._id,
      o.estatus,
      o.proveedor?.name,
      o.clientesId?.name,
    ].some((v) =>
      String(v ?? "")
        .toLowerCase()
        .includes(term),
    );
  });

  const totalPages = Math.max(
    1,
    Math.ceil(filteredOrdenes.length / itemsPerPage),
  );
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedOrdenes = filteredOrdenes.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleSave = async (formData) => {
    try {
      if (selectedOrden) {
        await axios.put(`${API_URL}/${selectedOrden._id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Orden actualizada correctamente");
      } else {
        await axios.post(API_URL, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Orden creada correctamente");
      }
      setShowModal(false);
      fetchOrders();
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Error al guardar la orden",
      );
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta orden de compra?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Orden eliminada correctamente");
      fetchOrders();
    } catch {
      toast.error("No se pudo eliminar la orden");
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-gray-800">
            Órdenes de Compra
          </h2>
          <p className="text-sm text-gray-500">
            {ordenes.length} orden{ordenes.length !== 1 ? "es" : ""}
          </p>
        </div>
        <Button
          onClick={() => {
            setSelectedOrden(null);
            setShowModal(true);
          }}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          + Nueva orden
        </Button>
      </div>

      {loading && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-700">
          <LoaderPinwheel className="h-4 w-4 animate-spin text-blue-600" />
          Cargando datos, por favor espera...
        </div>
      )}

      {/* Buscador */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar por ID, descripción, proveedor, cliente o estado…"
          className="w-full sm:w-96 pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-400 focus:outline-none"
        />
        {searchTerm && (
          <button
            onClick={() => setSearchTerm("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Tabla */}
      <div className="flex items-center justify-end mb-2">
        <PageSizeSelect
          value={itemsPerPage}
          onChange={(size) => {
            setItemsPerPage(size);
            setCurrentPage(1);
          }}
        />
      </div>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
              <th className="px-4 py-3 text-left">Descripción / ID</th>
              <th className="px-4 py-3 text-left">Estatus</th>
              <th className="px-4 py-3 text-left">Fecha</th>
              <th className="px-4 py-3 text-left">Proveedor</th>
              <th className="px-4 py-3 text-left">Cliente</th>
              <th className="px-4 py-3 text-right">Logística</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {paginatedOrdenes.length > 0 ? (
              paginatedOrdenes.map((o) => (
                <tr key={o._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <button
                      className="text-blue-600 hover:underline text-left font-medium"
                      onClick={() => navigate(`/detalleOC/${o._id}`)}
                    >
                      {o.descripcion || (
                        <span className="text-gray-400 italic">
                          Sin descripción
                        </span>
                      )}
                    </button>
                    <p className="text-xs text-gray-400 font-mono mt-0.5">
                      {o._id.slice(-10)}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${ESTATUS_STYLES[o.estatus] || "bg-gray-100 text-gray-600"}`}
                    >
                      {o.estatus}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">
                    {new Date(o.fecha).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {o.proveedor?.name || (
                      <span className="text-gray-300 italic">
                        Sin proveedor
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {o.clientesId?.name || "—"}
                  </td>
                  <td className="px-4 py-3 text-right text-gray-700">
                    {o.costologistica
                      ? `$${Number(o.costologistica).toLocaleString()}`
                      : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {o.estatus === "Creada" && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedOrden(o);
                            setShowModal(true);
                          }}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          className="bg-red-500 hover:bg-red-600 text-white"
                          onClick={() => handleDelete(o._id)}
                        >
                          Eliminar
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-12 text-center">
                  <Search className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">
                    No hay órdenes para la búsqueda actual
                  </p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
        <span className="text-xs text-gray-400">
          {filteredOrdenes.length === 0
            ? "0"
            : `${startIndex + 1}–${Math.min(startIndex + itemsPerPage, filteredOrdenes.length)}`}{" "}
          de {filteredOrdenes.length}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ← Anterior
          </Button>
          <span className="px-2 py-1 bg-gray-100 rounded-md text-xs">
            {currentPage} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Siguiente →
          </Button>
        </div>
      </div>

      {showModal && (
        <OrdenCompraForm
          orden={selectedOrden}
          onClose={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default OrdenesPage;
